import {
  Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { PasoRiel } from '../../altas/riel-pasos/riel-pasos.component';
import { cobrosIniciales } from '../uso-tecnologico';
import { cargosDeComercio } from '../cargos-comercio';
import { plazoDe, nombreDelPlazo, claveDelPlazo, claveDeFila, filaDeLaTabla, conUnidad, errorDelPlazo } from '../plazo-unidad';

/** Las claves de los pasos. Las usa `?paso=<clave>` en «Completar mi parte». */
export type ClavePasoCondiciones = 'plazo' | 'cobros' | 'modelos' | 'riesgo' | 'canales' | 'contrato' | 'revisar';

/**
 * Las condiciones comerciales, paso a paso (CONTRATO-REDISENO-ALTAS.md, «C»).
 *
 * 🔴 Es la MISMA lógica que tenía `comercios-condiciones-form` —plazo único, espejo
 * `commission.*`, cobros con el cargo por uso primero, modelos, el cálculo con la venta de
 * ejemplo, los cambios respecto de la vigente, las validaciones en la página, la confirmación en
 * dos pasos—, redistribuida en cinco pasos. No se reescribió ninguna regla: se movió.
 *
 * 🔴 REGLA DE JUAN, 2026-09-28 — «Si se selecciona otro plazo, es otra versión.» Una versión
 * lleva EXACTAMENTE un plazo de cobro con su comisión. La forma de cálculo, la base y el valor de
 * la comisión viajan con ese plazo, y el «espejo» `commission.*` que lee el listado de comercios
 * se arma acá igual al plazo (el núcleo lo vuelve a igualar al guardar y al leer).
 *
 * Dos modos:
 *   - `'version'` (la ruta «Nueva versión»): arranca copiada de `vigente`, compara contra ella y
 *     pide confirmación en dos pasos antes de emitir. El cascarón crea la versión.
 *   - `'propuesta'` (el alta, quien tenga la sección CONDICIONES — hoy el Gerente comercial):
 *     arranca de `semilla` (lo ya cargado) o vacía,
 *     no compara contra nada y emite directo. Quien la embebe hace el `PUT` de condiciones.
 * En los dos, `(guardado)` lleva el cuerpo con la forma de siempre (`armarCuerpo()`).
 *
 * El guardado lo hace el padre, así que el padre le devuelve cómo le fue: `[guardando]` pone el
 * botón en espera y `[error]` muestra el rechazo del servidor en la barra, sin perder lo escrito.
 */
/** Los momentos de «Servicio de cobranza» (VEN-14; CPC-26: ON_INSTALLMENT_PAID, cuando el cliente termina de pagar la cuota). */
const MOMENTOS_COBRANZA = ['ON_COLLECTION', 'ON_DUE_DATE', 'ON_INSTALLMENT_PAID'];

@Component({
  selector: 'app-condiciones-pasos',
  templateUrl: './condiciones-pasos.component.html',
  styleUrls: ['./condiciones-pasos.component.css']
})
export class CondicionesPasosComponent implements OnInit, OnChanges {

  @Input() modo: 'version' | 'propuesta' = 'version';
  /** El comercio (en 'version'): para el nombre y la clave del borrador. */
  @Input() comercio: any = null;
  /** La versión que rige hoy (en 'version'): de donde se copia y contra la que se compara. */
  @Input() vigente: any = null;
  /** Condiciones ya cargadas (en 'propuesta'): de donde se retoma. Misma forma que el cuerpo. */
  @Input() semilla: any = null;
  /** La clave del paso donde abre (`?paso=`). Si no es una de las cinco, abre en el primero. */
  @Input() pasoInicial: string | null | undefined = null;
  /** El número de la versión que se crea (en 'version'); si no llega, la vigente + 1. */
  @Input() proximoNumero: number | null = null;
  /** El padre está guardando: el botón queda en espera. */
  @Input() guardando = false;
  /** El rechazo del servidor, tal como lo va a leer quien carga. Se muestra en la barra. */
  @Input() error = '';
  /** Dónde guardar el borrador. Si no llega: el código del comercio ('version') o el `:id` de la ruta ('propuesta'). */
  @Input() claveBorrador: string | null = null;
  /** 'propuesta' (el alta): los canales que ya marcó el ejecutivo (sólo se leen: se cambian en su paso propio, no acá). */
  @Input() canales: string[] | null | undefined = null;
  /** 'propuesta': si el contrato firmado ya está cargado (el paso «Contrato firmado» lo pone el padre). */
  @Input() contratoCargado = false;
  /** La tienda del alta con su gerente (Juan, 2026-10-19): con ellos, el comercio entra con todo hecho. */
  @Input() tienda: { codigo?: string; caja?: number | null; gerente?: { nombre: string; correo: string } | null } | null | undefined = null;
  /** El código previsto del comercio, para proponer los usuarios (`<código>.caja<N>`, `<código>.gerente`). */
  @Input() codigoComercio: string | null | undefined = null;

  @Output() guardado = new EventEmitter<any>();
  @Output() cancelado = new EventEmitter<void>();
  /** Cada vez que cambia el paso, para el «Paso N de M» de quien la embebe. */
  @Output() cambioDePaso = new EventEmitter<{ clave: ClavePasoCondiciones; numero: number; total: number }>();

  @ViewChild('tituloPaso') tituloPaso?: ElementRef<HTMLElement>;

  constructor(private el: ElementRef<HTMLElement>, @Optional() private ruta: ActivatedRoute,
              @Optional() private merchants: MerchantService) { }

  // ══ TARIFARIO ESTÁNDAR (Mario, 07-oct-2026) ═════════════════════════════════
  // El plazo se elige de una tabla única: nombre, comisión, forma y base salen de ahí y no se
  // editan. Personalizar es «Condición especial», con motivo. El servidor aplica la misma regla.

  /** La tabla vigente (o la provisional del 05-oct). */
  tabla: any = null;
  errorTabla = '';
  /** La vigente tenía otra comisión para el mismo plazo: se avisa al pasar a la tabla. */
  avisoTabla = '';

  // PLZ-01/04 (Mario, 23-oct): el plazo es en días u horas; se busca en la tabla por unidad y valor y, si el plazo
  // tiene varias filas (TRF-10: Aliado preferencial / Aliado general), por nombre.
  get filaTabla(): any {
    return filaDeLaTabla(this.tabla?.rows, this.form.plazo);
  }

  claveDelPlazo = claveDelPlazo;
  claveDeFila = claveDeFila;

  /** El valor del plazo en su unidad (horas o días), para el campo de la condición especial. */
  get valorPlazo(): any { return plazoDe(this.form.plazo).valor ?? null; }
  set valorPlazo(v: any) {
    if (this.form.plazo.unit === 'HORAS') this.form.plazo.hours = v;
    else this.form.plazo.days = v;
  }

  /** Cambiar de unidad en la condición especial deja el valor vacío: 24 horas no es 24 días. */
  cambiarUnidad = (unidad: string): void => {
    this.form.plazo = { ...this.form.plazo, unit: unidad, days: null, hours: null };
  };

  private cargarTabla = async (): Promise<void> => {
    if (!this.merchants) return;
    try {
      this.tabla = await this.merchants.getRateTable(this.form.business_model);
      this.ajustarATabla(true);
    } catch (e: any) {
      this.errorTabla = 'No se pudo leer el tarifario estándar. Vuelve a intentar en un momento.';
    }
  };

  /** Fuera de la condición especial, el plazo queda exactamente como dice la tabla. */
  ajustarATabla = (alCargar = false): void => {
    if (this.form.especial?.activo || !this.tabla) return;
    const antes = { plazo: { ...this.form.plazo }, valor: this.form.plazo.commission_value };
    const fila = this.filaTabla;
    if (!fila) {
      if (alCargar && plazoDe(antes.plazo).valor) {
        this.avisoTabla = `La versión anterior usaba ${this.textoPlazo(antes.plazo)} al ${antes.valor} %, que no está en la tabla. Elige un plazo de la tabla o marca «Condición especial».`;
      }
      this.form.plazo.days = null; this.form.plazo.hours = null; this.form.plazo.unit = 'DIAS';
      this.form.plazo.label = ''; this.form.plazo.commission_value = null;
      return;
    }
    if (alCargar && antes.valor != null && Number(antes.valor) !== fila.commission_value) {
      this.avisoTabla = `La versión anterior cobraba ${antes.valor} % a ${this.textoPlazo(fila)}; según la tabla es ${fila.commission_value} %. Si debe seguir en ${antes.valor} %, marca «Condición especial».`;
    }
    this.aplicarFila(fila);
  };

  /** Al cambiar el tipo de contrato se carga su tabla; si el plazo elegido no está en ella, se vuelve a elegir. */
  cambiarTipo = async (): Promise<void> => {
    this.avisoTabla = ''; this.errorTabla = '';
    if (!this.merchants) return;
    try {
      this.tabla = await this.merchants.getRateTable(this.form.business_model);
      this.form.plazo.base = this.baseValida(this.form.plazo.base);
      this.ajustarATabla(false);
    } catch (e: any) { this.errorTabla = 'No se pudo leer el tarifario de ese tipo de contrato. Vuelve a intentar en un momento.'; }
  };

  elegirPlazo = (clave: any): void => {
    const fila = (this.tabla?.rows || []).find((r: any) => claveDeFila(r) === clave);
    if (fila) { this.aplicarFila(fila); this.avisoTabla = ''; }
  };

  /**
   * La base de la comisión. No viene de la tabla: se elige aparte, por omisión sobre el saldo a financiar.
   * CON-22 (cambiado el 21-oct): en cobranza, lo financiado o lo vendido; «lo cobrado» ya no (el núcleo lo rechaza).
   */
  baseValida = (base: any): string => {
    // Mario, 28-oct: en cobranza también «Descontado por liquidación» (SETTLED); en los otros modelos no se ofrece.
    if (this.form.business_model === 'COBRANZA') return ['SALE', 'SETTLED'].includes(base) ? base : 'FINANCED';
    return ['FINANCED', 'SALE', 'COLLECTED'].includes(base) ? base : 'FINANCED';
  };

  private aplicarFila = (fila: any): void => {
    const p = plazoDe(fila);
    this.form.plazo = { ...this.form.plazo, ...conUnidad(p.unidad, p.valor), hours: p.unidad === 'HORAS' ? p.valor : null,
      label: fila.label, commission_value: fila.commission_value, type_calc: 'PERCENTAGE',
      base: this.baseValida(this.form.plazo.base) };
  };

  /**
   * Un borrador guardado antes del 07-oct no trae los campos nuevos (condición especial, modelo de
   * negocio, regla de día hábil): se completan con lo de la vigente o con los valores por defecto.
   */
  private completarForm = (): void => {
    const base = this.semilla || this.vigente;
    if (!this.form.especial) this.form.especial = { activo: false, motivo: '' };
    if (!['COMPRA_DEUDA', 'COBRANZA'].includes(this.form.business_model)) {
      this.form.business_model = base?.business_model
        || (MOMENTOS_COBRANZA.includes(base?.settlement?.moment) ? 'COBRANZA' : 'COMPRA_DEUDA');
    }
    if (!MOMENTOS_COBRANZA.includes(this.form.moment)) this.form.moment = this.momentoDe(base);
    this.form.settlement = {
      business_day_rule: this.form.settlement?.business_day_rule || base?.settlement?.business_day_rule || 'NEXT_BUSINESS_DAY'
    };
    this.form.risk_model = { type_risk: 'NO_RECOURSE', recourse_days: null, guarantee_fund_pct: null };
  };

  /** Al apagar la condición especial, el plazo vuelve a la tabla. */
  cambiarEspecial = (activo: boolean): void => {
    this.form.especial.activo = activo;
    if (!activo) this.ajustarATabla();
  };

  get esPropuesta(): boolean { return this.modo === 'propuesta'; }

  // ══ LOS PASOS ═══════════════════════════════════════════════════════════════

  // Juan, 2026-10-08: un solo riel. «Modelos» (los canales) sale del contrato: en el alta es el paso
  // «Canales de venta» y en el comercio se cambia desde su ficha; «Modelo y liquidación» pasa a llamarse
  // «Esquema de liquidación»; el contrato firmado es un paso del mismo riel; la fecha la elige el aprobador final.
  // Mario, 11-oct: primero el tipo de contrato; de él salen los plazos y comisiones (una tabla por tipo).
  private readonly PASOS_PROPUESTA: { clave: ClavePasoCondiciones; nombre: string }[] = [
    { clave: 'riesgo', nombre: 'Tipo de contrato' },
    { clave: 'plazo', nombre: 'Plazo y comisión' },
    { clave: 'cobros', nombre: 'Cobros' },
    { clave: 'contrato', nombre: 'Contrato firmado' },
    { clave: 'revisar', nombre: 'Revisar' }
  ];
  private readonly PASOS_VERSION: { clave: ClavePasoCondiciones; nombre: string }[] = [
    { clave: 'riesgo', nombre: 'Tipo de contrato' },
    { clave: 'plazo', nombre: 'Plazo y comisión' },
    { clave: 'cobros', nombre: 'Cobros' },
    { clave: 'revisar', nombre: 'Revisar' }
  ];
  get PASOS(): { clave: ClavePasoCondiciones; nombre: string }[] {
    return this.esPropuesta ? this.PASOS_PROPUESTA : this.PASOS_VERSION;
  }

  /** Una frase por paso: qué se decide ahí. */
  fraseDelPaso = (clave: ClavePasoCondiciones): string => ({
    // TCP-06 (Mario, 30-oct): corta; antes repetía, con otras palabras, la ayuda que iba bajo el plazo.
    plazo: this.form?.business_model === 'COBRANZA'
      ? 'En cuánto tiempo se le paga cada cobro y qué comisión se le descuenta, según la tabla de cobranza.'
      : 'En cuánto tiempo se le paga cada venta y qué comisión se le descuenta, según la tabla de cesión de deuda.',
    cobros: 'Lo que se le cobra además de la comisión, empezando por el cargo por uso.',
    modelos: 'Por qué canales vende o cobra el comercio, y si alguno se le cobra aparte.',
    riesgo: 'Qué servicio se le presta: cesión de deuda o servicio de cobranza. De eso salen los plazos y comisiones del paso siguiente.',
    canales: 'Por dónde va a vender. No es parte del contrato: después se cambian desde la ficha del comercio.',
    contrato: 'El contrato que firmó el comercio con estas condiciones.',
    revisar: this.esPropuesta
      ? 'Lo que se pactó, antes de guardarlo. Desde cuándo opera el comercio lo elige el aprobador final.'
      : 'Desde cuándo rige la versión, qué cambia respecto de la vigente y cuánto sale una venta de ejemplo.'
  } as any)[clave];

  indice = 0;
  /** Hasta dónde se llegó: el riel deja volver a cualquier paso ya abierto. */
  maxAlcanzado = 0;

  get paso(): ClavePasoCondiciones { return this.PASOS[this.indice].clave; }
  get esUltimo(): boolean { return this.indice === this.PASOS.length - 1; }

  /** Lo que falta en un paso, dicho con todas las letras; '' si está completo. */
  faltaEnPaso = (clave: ClavePasoCondiciones): string => {
    const p = this.form.plazo;
    switch (clave) {
      case 'plazo':
        if (this.form.especial?.activo && !String(this.form.especial.motivo || '').trim()) {
          return 'Falta el motivo de la condición especial';
        }
        if (!this.form.especial?.activo && this.tabla && !this.filaTabla) return 'Elige un plazo de la tabla';
        if (!plazoDe(p).valor) return this.form.business_model === 'COBRANZA'
          ? 'Falta el plazo de cobro: cuántas horas o días después de cada cobro se le paga'
          : 'Falta el plazo de cobro: cuántos días después de la venta se le paga';
        if (errorDelPlazo(p.unit || 'DIAS', plazoDe(p).valor, this.form.business_model)) {
          return errorDelPlazo(p.unit || 'DIAS', plazoDe(p).valor, this.form.business_model);
        }
        if (p.commission_value === null || p.commission_value === '' || Number(p.commission_value) < 0) {
          return 'Falta la comisión de ese plazo';
        }
        return '';
      case 'modelos':
        return this.modelosInvalidos.length
          ? `Falta el monto del cobro de «${this.nombreModelo(this.modelosInvalidos[0].codigo)}»` : '';
      case 'riesgo':
        if (!['COMPRA_DEUDA', 'COBRANZA'].includes(this.form.business_model)) return 'Elige el modelo de negocio';
        return '';
      case 'revisar':
        if (this.esPropuesta) return '';
        if (!this.form.version.name?.trim()) return 'Falta el nombre de la versión';
        if (!this.form.version.date_init) return 'Falta desde cuándo rige';
        if (this.fechaInvalida) return 'La fecha de inicio no es válida';
        return '';
      default:
        return '';
    }
  };

  /** El primer paso que tiene algo pendiente, para mandar ahí desde «Revisar». */
  get pasoConFalta(): { clave: ClavePasoCondiciones; nombre: string } | null {
    return this.PASOS.find(p => p.clave !== 'revisar' && this.faltaEnPaso(p.clave)) || null;
  }

  /** Lo que muestra el riel debajo del nombre de cada paso ya decidido. */
  private resumenDelPaso = (clave: ClavePasoCondiciones): string | null => {
    switch (clave) {
      case 'plazo': {
        const v = this.form.plazo.commission_value;
        return `${this.textoPlazo(this.form.plazo)} · ${this.form.plazo.type_calc === 'FIXED' ? `$ ${v}` : `${v} %`}`;
      }
      case 'cobros': {
        const n = this.form.charges.length + 1;
        return n === 1 ? '1 cobro' : `${n} cobros`;
      }
      case 'modelos': {
        const n = this.form.modelos.filter((m: any) => m.usa).length;
        return n === 1 ? '1 en uso' : `${n} en uso`;
      }
      case 'riesgo':
        return this.eti(this.form.business_model);
      case 'canales': {
        const n = this.form.canales.length;
        return n === 1 ? '1 canal' : `${n} canales`;
      }
      case 'contrato':
        return this.contratoCargado ? 'cargado' : 'falta';
      default:
        return null;
    }
  };

  get riel(): PasoRiel[] {
    return this.PASOS.map((p, i) => {
      if (i === this.indice) return { clave: p.clave, nombre: p.nombre, estado: 'ACTUAL' };
      if (i > this.maxAlcanzado) return { clave: p.clave, nombre: p.nombre, estado: 'PENDIENTE' };
      const falta = this.faltaEnPaso(p.clave);
      return falta
        ? { clave: p.clave, nombre: p.nombre, detalle: 'Falta un dato', estado: 'FALLO' }
        : { clave: p.clave, nombre: p.nombre, detalle: this.resumenDelPaso(p.clave), estado: 'HECHO' };
    });
  }

  irA = (clave: string): void => {
    const i = this.PASOS.findIndex(p => p.clave === clave);
    if (i < 0 || i === this.indice) return;
    this.indice = i;
    this.maxAlcanzado = Math.max(this.maxAlcanzado, i);
    this.confirmandoCrear = false;
    this.alCambiarDePaso();
  };

  siguiente = (): void => {
    if (this.esUltimo || this.faltaEnPaso(this.paso)) return;
    this.irA(this.PASOS[this.indice + 1].clave);
  };

  anterior = (): void => {
    if (this.indice > 0) this.irA(this.PASOS[this.indice - 1].clave);
  };

  private alCambiarDePaso = (): void => {
    this.guardarBorrador();
    this.avisarPaso();
    // El paso nuevo arranca arriba, con el foco en su título (el riel queda a la vista).
    setTimeout(() => {
      const arriba = this.el.nativeElement.getBoundingClientRect().top;
      if (arriba < 0) window.scrollTo({ top: window.scrollY + arriba - 80 });
      this.tituloPaso?.nativeElement.focus({ preventScroll: true });
    });
  };

  private avisarPaso = (): void => {
    this.cambioDePaso.emit({ clave: this.paso, numero: this.indice + 1, total: this.PASOS.length });
  };

  // ══ EL FORMULARIO ═══════════════════════════════════════════════════════════

  /** La base copiada tenía varias filas de plazo (antes de «un plazo por versión»). Se dice. */
  vigenteConVariosPlazos = false;

  /**
   * Simulador. Existe porque la elección de la BASE es donde está el dinero y no es evidente:
   * con 50 de inicial sobre 200, «monto vendido» y «saldo a financiar» se llevan un 33% de
   * diferencia. Verlo en bolívares evita el error.
   */
  sim = { venta: 200, inicial: 50 };

  // ══ LOS CANALES DE VENTA (alta) ══════════════════════════════════════════════
  readonly CANALES_DEF: { codigo: string; nombre: string; detalle: string; fijo?: boolean }[] = [
    { codigo: 'PORTAL', nombre: 'Portal del comercio', detalle: 'Donde entra el comercio a administrar su negocio.', fijo: true },
    // Juan, 2026-10-19: «vender como cajero no es una modalidad, el portal es una modalidad». Se oculta,
    // pero CAJA sigue marcado siempre (ver CANALES_SIEMPRE).
    // { codigo: 'CAJA', nombre: 'Vender como cajero', detalle: 'El cajero vende y cobra la inicial desde el portal.' },
    { codigo: 'LINK_PAGO', nombre: 'Link de pago', detalle: 'Vende en línea: el cliente acepta y paga desde su teléfono.' },
    { codigo: 'INTEGRACION', nombre: 'Integración por API', detalle: 'Su sistema de caja vende por la pasarela de CrediTotal.' },
    { codigo: 'TIENDA_EN_LINEA', nombre: 'Tienda en línea', detalle: 'Su tienda web cobra con CrediTotal.' }
  ];

  /** Canales que van marcados aunque no se muestren. */
  readonly CANALES_SIEMPRE = ['CAJA'];

  tieneCanal = (codigo: string): boolean => this.form.canales.includes(codigo);

  alternarCanal = (c: { codigo: string; fijo?: boolean }): void => {
    if (c.fijo) return;
    this.form.canales = this.tieneCanal(c.codigo)
      ? this.form.canales.filter((x: string) => x !== c.codigo)
      : this.CANALES_DEF.map(d => d.codigo).filter(k => k === c.codigo || this.form.canales.includes(k));
    this.form.canales = [...new Set([...this.form.canales, ...this.CANALES_SIEMPRE])];
    this.guardarBorrador();
  };

  get nombresCanales(): string {
    return this.CANALES_DEF.filter(d => this.tieneCanal(d.codigo)).map(d => d.nombre).join(' · ');
  }

  /** Lo que el portal le va a pedir al comercio en su primer ingreso, según sus canales. */
  get loQueSeLePide(): string {
    const p = ['una tienda', 'una caja con su usuario', 'un gerente de tienda'];
    if (this.tieneCanal('LINK_PAGO')) p.push('un vendedor');
    return p.join(', ');
  }

  /**
   * Juan, 2026-10-19: «el comercio va a entrar limpio ya con eso creado». Si vende por caja y el ejecutivo cargó
   * a su gerente, el comercio encuentra hechos la tienda, la caja y los usuarios del gerente y de la caja.
   */
  get entraConTodo(): boolean {
    const t = this.tienda;
    return this.tieneCanal('CAJA') && !!(t && t.codigo && t.gerente?.correo);
  }
  get usuarioCajero(): string { return `${String(this.codigoComercio || '').toLowerCase()}.caja${this.tienda?.caja || 1}`; }
  get usuarioGerente(): string { return `${String(this.codigoComercio || '').toLowerCase()}.gerente`; }

  get pideTecnico(): boolean { return this.tieneCanal('INTEGRACION') || this.tieneCanal('TIENDA_EN_LINEA'); }

  /** Una frase que dice qué significa lo elegido como cargo por uso, con la venta de ejemplo. */
  get fraseCargo(): string {
    const cargo = this.fijoSimulado();
    if (!cargo) return 'Sin cargo por uso.';
    const n = (x: number) => x.toFixed(2).replace('.', ',');
    return this.form.flat_fee.charged_with === 'INITIAL'
      ? `Se cobra con la inicial: en una venta de ${this.sim.venta} USD con ${this.sim.inicial} de inicial, el cliente paga en caja ${n(this.sim.inicial + cargo)} USD.`
      : `Se descuenta al liquidar: el cliente paga sólo la inicial y al comercio se le descuentan ${n(cargo)} USD de lo que se le paga.`;
  }

  /** Momento de liquidación de una versión: ON_DUE_DATE u ON_INSTALLMENT_PAID solo si así quedó; lo demás es «al cobrar». */
  momentoDe = (v: any): string => (MOMENTOS_COBRANZA.includes(v?.settlement?.moment) ? v.settlement.moment : 'ON_COLLECTION');

  // ── «Plazo y comisión», propuesta C (Mario, 30-oct): las opciones y el «Así queda» del lado ──────────────────

  /** La nota se abre a pedido; si ya trae texto, se ve. */
  verNota = false;

  /** Las bases que se ofrecen según el tipo (CON-22, 21-oct; SETTLED solo en cobranza, Mario 28-oct). */
  get opcionesBase(): { v: string; t: string; d: string }[] {
    const comunes = [
      { v: 'FINANCED', t: 'Saldo a financiar', d: 'Lo vendido menos la inicial.' },
      { v: 'SALE', t: 'Monto vendido', d: 'Incluye la inicial.' }
    ];
    return this.form.business_model === 'COBRANZA'
      ? [...comunes, { v: 'SETTLED', t: 'Cada liquidación', d: 'La comisión se descuenta sobre el total de cada liquidación, por cartera, redondeada una sola vez. El flat y el uso tecnológico, una vez por compra, sobre su base.' }]
      : [...comunes, { v: 'COLLECTED', t: 'Lo cobrado', d: 'Sobre lo que el cliente va pagando.' }];
  }

  get ayudaBase(): string {
    return (this.opcionesBase.find(b => b.v === this.form.plazo.base) || this.opcionesBase[0]).d;
  }

  /** Desde cuándo corre el plazo: en cobranza, según el momento del paso «Tipo de contrato»; en cesión, la venta. */
  get desdeCuando(): string {
    if (this.form.business_model !== 'COBRANZA') return 'después de la venta';
    return ({ ON_DUE_DATE: 'después del vencimiento de la cuota', ON_INSTALLMENT_PAID: 'después de que el cliente termina de pagar la cuota' } as any)[this.form.moment]
      || 'después de cada cobro';
  }

  /** El «Así queda» del paso; null mientras no haya plazo ni comisión. */
  get asiQuedaPlazo(): { comision: string; plazo: string; cuando: string; base: string; habil: string } | null {
    const p = this.form.plazo, v = p?.commission_value;
    if (!p || v === null || v === undefined || v === '' || !plazoDe(p).valor) return null;
    return {
      comision: p.type_calc === 'FIXED' ? `$ ${v}` : `${v} %`,
      plazo: p.label?.trim() || this.textoPlazo(p),
      cuando: `${this.textoPlazo(p)} ${this.desdeCuando}`,
      base: (this.opcionesBase.find(b => b.v === p.base)?.t || this.eti(p.base)).toLowerCase(),
      habil: this.form.settlement.business_day_rule === 'SAME_DAY' ? 'ese mismo día' : 'día hábil siguiente'
    };
  }

  /** La línea de tiempo del resumen de «Tipo de contrato» (propuesta C, Mario 30-oct): qué pasa → se le liquida. */
  get hitosModelo(): { ico: string; tit: string; sub: string }[] {
    const plazos = 'En los plazos comprometidos, menos la comisión';
    if (this.form.business_model !== 'COBRANZA') {
      return [
        { ico: 'fa-file-contract', tit: 'La venta queda firme', sub: 'Le cede la deuda a CrediTotal' },
        { ico: 'fa-hand-holding-usd', tit: 'Se le paga lo financiado', sub: plazos }
      ];
    }
    const momento = this.momentoDe({ settlement: { moment: this.form.moment } });
    const hito = momento === 'ON_DUE_DATE'
      ? { ico: 'fa-calendar-day', tit: 'Vence la cuota', sub: 'Pague o no el cliente' }
      : momento === 'ON_INSTALLMENT_PAID'
        ? { ico: 'fa-check-double', tit: 'El cliente termina de pagar la cuota', sub: 'Lo que pagó el cliente' }
        : { ico: 'fa-coins', tit: 'El cliente paga (cada abono)', sub: 'Lo que pagó el cliente' };
    return [hito, { ico: 'fa-hand-holding-usd', tit: 'Se le liquida al comercio', sub: plazos }];
  }

  // Sin plazo (Mario, 30-oct: «alli en ningun lado se deben ver 24 horas»). Antes: «… a las 24 h de su vencimiento …»
  // y, en cesión, «A los <plazo> de cada venta …».
  get fraseModelo(): string {
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_DUE_DATE') {
      return 'Se le liquida cada cuota completa cuando vence, pague o no el cliente.';
    }
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_INSTALLMENT_PAID') {
      return 'Se le liquida cada cuota cuando el cliente termina de pagarla; si queda a medias, todavía no se liquida.';
    }
    return this.form.business_model === 'COBRANZA'
      ? 'Se le liquida lo que los clientes vayan pagando; si un cliente no paga, esa parte no se liquida.'
      : 'Se le paga lo financiado de cada venta, menos la comisión, aunque el cliente todavía no haya pagado sus cuotas.';
  }

  /** Los cinco modelos por los que puede vender o cobrar un comercio (regla de Juan, 2026-09-28). */
  readonly MODELOS_DEF: { codigo: string; nombre: string }[] = [
    { codigo: 'PORTAL', nombre: 'Portal del comercio' },
    { codigo: 'CAJA', nombre: 'Caja' },
    { codigo: 'LINK_PAGO', nombre: 'Link de pago' },
    { codigo: 'INTEGRACION', nombre: 'Integración por API' },
    { codigo: 'TIENDA_EN_LINEA', nombre: 'Tienda en línea' }
  ];

  private modeloVacio = () => this.MODELOS_DEF.map(d => ({
    codigo: d.codigo, usa: false, cobra: false,
    cargo: { type_calc: 'FIXED', value: null, charged_with: 'SETTLEMENT' }
  }));

  private formVacio = (): any => ({
    version: { name: '', date_init: '' },
    // El único plazo de esta versión: días, forma de cálculo, valor y base viajan juntos.
    plazo: { unit: 'DIAS', days: null, hours: null, label: '', type_calc: 'PERCENTAGE', commission_value: null,
             base: 'FINANCED', notes: '' },
    // Personalizar el plazo es un caso especial, con motivo (tarifario estándar, 07-oct).
    especial: { activo: false, motivo: '' },
    risk_model: { type_risk: 'NO_RECOURSE', recourse_days: null, guarantee_fund_pct: null },
    // MODELO Y LIQUIDACIÓN (minuta 07-oct): el modelo decide cómo se liquida; el plazo, cuándo.
    // Ya no hay momento ni días de diferimiento: la fecha de pago es venta firme + plazo.
    business_model: 'COMPRA_DEUDA',
    // Cobranza de MT · fase 2 (VEN-14): solo cuenta si el modelo es COBRANZA.
    moment: 'ON_COLLECTION',
    settlement: { business_day_rule: 'NEXT_BUSINESS_DAY' },
    // Juan, 2026-10-08: monto fijo o % de la venta, y por omisión se cobra con la inicial.
    flat_fee: { value: 0, charged_with: 'INITIAL', type_calc: 'FIXED' },
    charges: [] as any[],
    modelos: this.modeloVacio(),
    canales: ['PORTAL', 'CAJA'] as string[],
    contract_reference: ''
  });

  form: any = this.formVacio();

  private iniciado = false;

  ngOnInit(): void {
    this.prepararForm();
    // Con algo de donde partir (la vigente o lo ya cargado), todos los pasos están llenos: el riel
    // deja ir a cualquiera. Vacía, se avanza en orden.
    const base = this.semilla || this.vigente;
    this.maxAlcanzado = base ? this.PASOS.length - 1 : 0;

    this.retomarBorrador();
    this.completarForm();
    this.cargarTabla();

    const i = this.PASOS.findIndex(p => p.clave === this.pasoInicial);
    if (i >= 0) { this.indice = i; this.maxAlcanzado = Math.max(this.maxAlcanzado, i); }
    this.iniciado = true;
    // Después del primer ciclo, así el padre no cambia su vista mientras se está pintando.
    Promise.resolve().then(() => this.avisarPaso());
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (!this.iniciado) return;
    // «Completar» desde otra pantalla con la misma ruta: `?paso=` cambia sin volver a crearla.
    if (cambios['pasoInicial'] && this.pasoInicial) this.irA(this.pasoInicial);
    // El servidor rechazó: se sale de la confirmación y lo escrito vuelve al borrador.
    if (cambios['error'] && this.error) {
      this.confirmandoCrear = false;
      this.guardarBorrador();
    }
  }

  /** «2026-09-28», en fecha LOCAL. Con `toISOString()` —que pasa a UTC— a las 11 de la noche en
   *  Venezuela ya era el día siguiente y una fecha «de hoy» salía «de mañana» (se vio: 2026-09-24). */
  private formatoFecha = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  get numeroVersion(): number {
    return this.proximoNumero ?? ((this.vigente?.version?.number || 0) + 1);
  }

  /** Arma el form: en 'version' desde la vigente; en 'propuesta' desde la semilla. */
  private prepararForm = (): void => {
    this.form = this.formVacio();
    this.vigenteConVariosPlazos = false;
    const base = this.semilla || this.vigente;

    if (this.esPropuesta) {
      // No hay una vigente que se cierra: si ya había condiciones cargadas, se retoma su
      // nombre y su fecha tal cual (es la MISMA propuesta, no una versión nueva); si no, un
      // nombre genérico y hoy.
      this.form.version.name = base?.version?.name?.trim() || 'Condiciones del comercio';
      this.form.version.date_init = base?.version?.date_init
        ? this.formatoFecha(new Date(base.version.date_init)) : this.formatoFecha(new Date());
      // Los canales que ya marcó el ejecutivo (vienen aparte de las condiciones; sólo se leen).
      if (Array.isArray(this.canales) && this.canales.length) this.form.canales = [...new Set([...this.canales, ...this.CANALES_SIEMPRE])];
    } else {
      this.form.version.name = `Condiciones v${this.numeroVersion}`;
      // Por omisión: HOY si es la primera versión (sin condición el comercio no puede vender, así
      // que rige desde ya); MAÑANA si ya hay una vigente (la nueva tiene que ser posterior).
      const d = new Date();
      if (this.vigente) d.setDate(d.getDate() + 1);
      this.form.version.date_init = this.formatoFecha(d);
    }

    // Arranca con lo que rige hoy (o lo ya propuesto), así se cambia solo lo que cambia.
    if (base) {
      this.form.risk_model = { ...this.form.risk_model, ...(base.risk_model || {}) };
      this.form.settlement = { business_day_rule: base.settlement?.business_day_rule || 'NEXT_BUSINESS_DAY' };
      this.form.business_model = base.business_model
        || (MOMENTOS_COBRANZA.includes(base.settlement?.moment) ? 'COBRANZA' : 'COMPRA_DEUDA');
      this.form.moment = this.momentoDe(base);
      this.form.risk_model = { type_risk: 'NO_RECOURSE', recourse_days: null, guarantee_fund_pct: null };
      this.form.flat_fee = { ...this.form.flat_fee, ...(base.flat_fee || {}) };
      if (!this.form.flat_fee.type_calc) this.form.flat_fee.type_calc = 'FIXED';
      // los conceptos se copian: casi siempre siguen igual
      this.form.contract_reference = base.contract_reference || '';

      // ── el plazo: de settlement.rates si hay, o del esquema viejo si no ──
      const ratesBase = (base.settlement?.rates || []).filter((r: any) => r.status !== false);
      this.vigenteConVariosPlazos = ratesBase.length > 1;
      let p: any = ratesBase.find((r: any) => r.is_default) || ratesBase[0] || null;
      if (!p && base.commission?.value != null) {
        // 🔴 Esquema viejo: una comisión única y una frecuencia. Se siembra el
        // plazo con ese mismo valor, así la próxima versión queda migrada.
        const dias = ({ DAILY: 1, WEEKLY: 7, BIWEEKLY: 15, MONTHLY: 30 } as any)
          [base.settlement?.frequency] || 7;
        p = { days: dias, type_calc: base.commission?.type_calc || 'PERCENTAGE',
              commission_value: base.commission.value,
              base: base.commission?.base || 'FINANCED' };
      }
      if (p) {
        this.form.plazo = {
          unit: p.unit || 'DIAS', days: p.days ?? null, hours: p.unit === 'HORAS' ? (p.hours ?? null) : null,
          label: (p.label || '').trim(),
          type_calc: p.type_calc || 'PERCENTAGE',
          commission_value: p.commission_value ?? null,
          base: p.base || 'FINANCED',
          notes: (p.notes || '').trim()
        };
      }

      // ── los modelos: se copian por código; los que no viajaban quedan sin usar ──
      const modelosBase = base.modelos || [];
      this.form.modelos = this.MODELOS_DEF.map(d => {
        const mv = modelosBase.find((x: any) => x.codigo === d.codigo);
        return mv
          ? { codigo: d.codigo, usa: !!mv.usa, cobra: !!mv.usa && !!mv.cobra,
              cargo: { type_calc: mv.cargo?.type_calc || 'FIXED',
                       value: mv.cargo?.value ?? null,
                       charged_with: mv.cargo?.charged_with || 'SETTLEMENT' } }
          : { codigo: d.codigo, usa: false, cobra: false,
              cargo: { type_calc: 'FIXED', value: null, charged_with: 'SETTLEMENT' } };
      });
    }

    // Mario, 27-oct: el uso tecnológico viene por defecto (editable, pausable, quitable). Los conceptos de la
    // versión de partida se copian tal cual: casi siempre siguen igual. Solo se precarga aquí, al iniciar:
    // si se quita a propósito no reaparece (el borrador guarda el form sin él).
    this.form.charges = cobrosIniciales(base?.charges, base?.settlement?.rates);
  };

  nombreModelo = (codigo: string): string =>
    this.MODELOS_DEF.find(d => d.codigo === codigo)?.nombre || codigo;

  // ── qué campos hacen falta según el modelo de riesgo ────────────────────────

  /** Las condiciones con recurso o fondo de garantía se retiraron (minuta 07-oct): siempre sin recurso. */
  get pideDiasRecurso(): boolean {
    return false;
  }
  get pideFondo(): boolean { return false; }

  /** Los modelos marcados «se cobra» pero sin monto cargado: no se puede crear así. */
  get modelosInvalidos(): any[] {
    return this.form.modelos.filter((m: any) => m.usa && m.cobra &&
      (m.cargo.value === null || m.cargo.value === '' || Number(m.cargo.value) < 0));
  }

  get valido(): boolean {
    const p = this.form.plazo;
    // Sin plazo no hay comisión: es lo único que no puede faltar.
    if (errorDelPlazo(p.unit || 'DIAS', plazoDe(p).valor, this.form.business_model)) return false;
    if (p.commission_value === null || p.commission_value === '' || Number(p.commission_value) < 0) return false;
    if (this.faltaEnPaso('plazo')) return false;
    if (!this.form.version.date_init) return false;
    if (!this.form.version.name?.trim()) return false;
    if (this.pideDiasRecurso && !this.form.risk_model.recourse_days) return false;
    if (this.pideFondo && !this.form.risk_model.guarantee_fund_pct) return false;
    if (this.modelosInvalidos.length) return false;
    return true;
  }

  /**
   * Lo que falta para poder crear, dicho con todas las letras, en el orden de los pasos.
   * 📍 La barra del pie decía «Faltan datos obligatorios» y obligaba a recorrer las secciones
   * para descubrir cuál.
   */
  get faltaParaCrear(): string {
    for (const p of this.PASOS) {
      const f = this.faltaEnPaso(p.clave);
      if (f) return f;
    }
    return 'Faltan datos obligatorios';
  }

  /** La fecha tiene que ser posterior al inicio de la vigente: el back lo exige. En 'propuesta'
   *  no hay una vigente que se cierre, así que esta regla no aplica. */
  get fechaInvalida(): boolean {
    if (this.esPropuesta || !this.vigente || !this.form.version.date_init) return false;
    return new Date(this.form.version.date_init) <= new Date(this.vigente.version.date_init);
  }

  // ── presentación de lo vigente, para comparar ───────────────────────────────

  comisionVigente = (): string => {
    const c = this.vigente?.commission;
    if (!c || c.value === null || c.value === undefined) return 'sin definir';
    return (c.type_calc === 'PERCENTAGE' ? `${c.value} %` : `$ ${c.value}`) +
           ' ' + (({
      SALE:      'sobre el monto vendido',
      FINANCED:  'sobre el saldo a financiar',
      COLLECTED: 'sobre lo cobrado',
      SETTLED:   'descontado por liquidación'
    } as any)[c.base] || 'sobre el monto vendido');
  };

  riesgoVigente = (): string => ({
    NO_RECOURSE: 'Sin recurso', RECOURSE: 'Con recurso', MIXED: 'Mixto'
  } as any)[this.vigente?.risk_model?.type_risk] || '—';

  // ══ GUARDAR ═════════════════════════════════════════════════════════════════

  /** El «¿Crear la versión nueva?» es un panel en la misma barra, en dos pasos (solo 'version'). */
  confirmandoCrear = false;

  get puedeGuardar(): boolean { return this.valido && !this.fechaInvalida && !this.guardando; }

  pedirConfirmacion = (): void => {
    if (!this.puedeGuardar) return;
    this.confirmandoCrear = true;
  };

  cancelarConfirmacion = (): void => { this.confirmandoCrear = false; };

  /**
   * Arma el cuerpo tal como lo exige el núcleo — es la MISMA forma para crear una versión de
   * `merchant_terms` y para `PUT .../condiciones` del alta (contrato de Juan, 2026-09-28): se
   * arma una sola vez y cada modo decide qué hacer con él.
   */
  private armarCuerpo = (): any => {
    const datos: any = {
        version: { name: this.form.version.name.trim(), date_init: this.form.version.date_init },
        // 🔴 `commission.*` es el espejo del ÚNICO plazo de esta versión — se
        // conserva porque el listado de comercios y los reportes lo leen. El
        // único lugar donde se define cuánto se cobra es el plazo.
        commission: {
          type_calc: this.form.plazo.type_calc,
          value: Number(this.form.plazo.commission_value) || 0,
          base: this.form.plazo.base
        },
        business_model: this.form.business_model,
        risk_model: { type_risk: 'NO_RECOURSE' },
        // Tarifario estándar: sin especial, el servidor toma de la tabla nombre y comisión del plazo.
        special: this.form.especial.activo ? { motivo: String(this.form.especial.motivo || '').trim() } : undefined,
        settlement: {
          moment: this.form.business_model === 'COBRANZA' ? this.momentoDe({ settlement: { moment: this.form.moment } }) : undefined,
          business_day_rule: this.form.settlement.business_day_rule || 'NEXT_BUSINESS_DAY',
          // Una versión lleva UN solo plazo activo — regla de Juan, 2026-09-28.
          rates: [{
            // PLZ-04: el plazo con su unidad (en horas manda `hours` y `days` como redondeo hacia arriba).
            ...conUnidad(this.form.plazo.unit || 'DIAS', plazoDe(this.form.plazo).valor),
            label: (this.form.plazo.label || '').trim() || undefined,
            type_calc: this.form.plazo.type_calc || 'PERCENTAGE',
            commission_value: Number(this.form.plazo.commission_value) || 0,
            base: this.baseValida(this.form.plazo.base),
            notes: (this.form.plazo.notes || '').trim() || undefined,
            is_default: true,
            status: true
          }]
          // La `frequency` no se manda: el núcleo la deriva del plazo (1→DAILY,
          // ≤7→WEEKLY, ≤15→BIWEEKLY, si no MONTHLY) y no se muestra acá.
        },
        flat_fee: {
          value: Number(this.form.flat_fee.value) || 0,
          charged_with: this.form.flat_fee.charged_with,
          type_calc: this.form.flat_fee.type_calc === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED'
        },
        charges: this.form.charges
          .filter((c: any) => c.concept?.trim())
          .map((c: any) => ({
            concept: c.concept.trim(),
            description: (c.description || '').trim() || undefined,
            type_calc: c.type_calc,
            value: Number(c.value) || 0,
            base: c.base,
            charged_with: c.charged_with,
            status: c.status !== false
          })),
        // Los cinco modelos, siempre los cinco: una versión nueva también registra
        // el que DEJÓ de usarse o de cobrarse, no sólo el que se agrega.
        modelos: this.form.modelos.map((m: any) => ({
          codigo: m.codigo,
          usa: !!m.usa,
          cobra: !!m.usa && !!m.cobra,
          ...(m.usa && m.cobra ? { cargo: {
            type_calc: m.cargo.type_calc,
            value: Number(m.cargo.value) || 0,
            charged_with: m.cargo.charged_with
          } } : {})
        })),
        contract_reference: (this.form.contract_reference || '').trim() || null
    };
    // En el alta los canales viajan aparte (no son parte del contrato) y la fecha la pone el aprobador
    // final: no se manda `modelos` ni `version.date_init`.
    if (this.esPropuesta) {
      // Juan, 2026-10-23: los canales ya no viajan acá: los marca el ejecutivo (PUT /canales) y el gerente los cambia ahí.
      delete datos.modelos;
      datos.version = { name: this.form.version.name.trim() || 'Condiciones del comercio' };
    }
    if (this.pideDiasRecurso) datos.risk_model.recourse_days = Number(this.form.risk_model.recourse_days);
    if (this.pideFondo) datos.risk_model.guarantee_fund_pct = Number(this.form.risk_model.guarantee_fund_pct);
    return datos;
  };

  /** Emite el cuerpo. 'propuesta' llega acá directo; 'version', desde «Sí, crear la versión». */
  guardar = (): void => {
    if (!this.puedeGuardar) return;
    const datos = this.armarCuerpo();
    // Se borra al emitir; si el servidor rechaza, `[error]` lo vuelve a guardar (ngOnChanges).
    this.borrarBorrador();
    this.guardado.emit(datos);
  };

  cancelar = (): void => {
    this.borrarBorrador();
    this.cancelado.emit();
  };

  // ══ BORRADOR (sessionStorage) ═══════════════════════════════════════════════
  //
  // Lo cargado y no guardado sobrevive a recargar la página o a ir y volver. Se guarda junto con
  // una «firma» de lo que había de base (la vigente o la semilla): si la base cambió mientras
  // tanto —otra versión creada, otra propuesta guardada—, el borrador es de otra cosa y se tira.

  /** Cuándo se había guardado el borrador que se retomó (para decirlo); null si no se retomó. */
  borradorRetomado: string | null = null;

  private get llaveBorrador(): string | null {
    if (this.claveBorrador) return `condiciones-pasos:${this.claveBorrador}`;
    if (!this.esPropuesta && this.comercio?.code) return `condiciones-pasos:version:${this.comercio.code}`;
    const id = this.ruta?.snapshot?.paramMap?.get('id');
    return this.esPropuesta && id ? `condiciones-pasos:alta:${id}` : null;
  }

  private get firmaBase(): string {
    return JSON.stringify(this.esPropuesta ? (this.semilla ?? null) : (this.vigente?.version?.code ?? null));
  }

  guardarBorrador = (): void => {
    const llave = this.llaveBorrador;
    if (!llave) return;
    try {
      sessionStorage.setItem(llave, JSON.stringify({
        firma: this.firmaBase, form: this.form, sim: this.sim,
        paso: this.paso, maxAlcanzado: this.maxAlcanzado, en: new Date().toISOString()
      }));
    } catch { /* sin almacenamiento, la pantalla anda igual */ }
  };

  private borrarBorrador = (): void => {
    const llave = this.llaveBorrador;
    if (!llave) return;
    try { sessionStorage.removeItem(llave); } catch { /* idem */ }
  };

  private retomarBorrador = (): void => {
    const llave = this.llaveBorrador;
    if (!llave) return;
    try {
      const b = JSON.parse(sessionStorage.getItem(llave) || 'null');
      if (!b?.form || b.firma !== this.firmaBase) { sessionStorage.removeItem(llave); return; }
      this.form = b.form;
      this.form.canales = [...new Set([...(this.form.canales || []), ...this.CANALES_SIEMPRE])];
      if (b.sim) this.sim = b.sim;
      const i = this.PASOS.findIndex(p => p.clave === b.paso);
      if (i >= 0) this.indice = i;
      this.maxAlcanzado = Math.max(this.maxAlcanzado, Number(b.maxAlcanzado) || 0, this.indice);
      this.borradorRetomado = b.en || null;
    } catch { /* un borrador ilegible no frena nada */ }
  };

  /** «Empezar de nuevo»: se tira el borrador y se vuelve a lo de base. */
  descartarBorrador = (): void => {
    this.borrarBorrador();
    this.borradorRetomado = null;
    this.prepararForm();
  };

  // ══ SIMULADOR ═══════════════════════════════════════════════════════════════

  get saldoAFinanciar(): number {
    return Math.max(0, (Number(this.sim.venta) || 0) - (Number(this.sim.inicial) || 0));
  }

  /** Sobre qué monto se aplica el porcentaje, según la base elegida. */
  montoBase = (base?: string): number => {
    const b = base || this.form.plazo.base;
    if (b === 'SALE') return Number(this.sim.venta) || 0;
    // FINANCED y COLLECTED se calculan sobre el saldo: lo que el cliente va a
    // pagar en cuotas. COLLECTED es una estimación al final de la vida del
    // crédito, no un monto del día de la venta — y eso hay que decirlo.
    // SETTLED (28-oct): la comisión real es un % de lo liquidado, por liquidación y cartera, redondeado una vez;
    // para el ejemplo se trata como el saldo financiado (en cobranza lo liquidado sale de lo financiado).
    return this.saldoAFinanciar;
  };

  /**
   * Cuánto sale la comisión sobre la venta simulada.
   * Sin argumento: la del plazo que se está cargando. Con argumento —una condición ya guardada,
   * como la vigente— usa la forma `commission` de esa condición.
   */
  comisionSimulada = (c?: any): number => {
    if (!c) {
      const v = Number(this.form.plazo.commission_value) || 0;
      if (this.form.plazo.type_calc === 'FIXED') return v;
      return this.montoBase(this.form.plazo.base) * v / 100;
    }
    const v = Number(c?.value) || 0;
    if (c?.type_calc === 'FIXED') return v;
    return this.montoBase(c?.base) * v / 100;
  };

  fijoSimulado = (f?: any): number => {
    const ff = f || this.form.flat_fee;
    const v = Number(ff?.value) || 0;
    // Juan, 2026-10-08: el cargo puede ser un % de la venta.
    return ff?.type_calc === 'PERCENTAGE' ? Math.round(this.sim.venta * v) / 100 : v;
  };

  totalSimulado = (c?: any, f?: any): number =>
    this.comisionSimulada(c) + this.fijoSimulado(f) + this.totalConceptos;

  /** Cuándo entra cada plata. Es la mitad de la información que faltaba. */
  get cuandoComision(): string {
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_DUE_DATE') return 'después que vence la cuota';
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_INSTALLMENT_PAID') return 'después que el cliente termina de pagar la cuota';
    return this.form.business_model === 'COBRANZA'
      ? 'a medida que el cliente paga' : 'al registrarse la venta';
  }

  get cuandoFijo(): string {
    return this.form.flat_fee.charged_with === 'INITIAL'
      ? 'en la venta, junto con la inicial' : 'al liquidarle al comercio';
  }

  /** El total simulado con la condición que rige hoy, para poder comparar. */
  get totalVigente(): number | null {
    if (!this.vigente) return null;
    return this.comisionSimulada(this.vigente.commission)
         + this.fijoSimulado(this.vigente.flat_fee)
         + this.totalConceptosDe(this.vigente.charges);
  }

  get diferencia(): number {
    const v = this.totalVigente;
    return v === null ? 0 : this.totalSimulado() - v;
  }

  // ══ QUÉ CAMBIA respecto de la vigente ═══════════════════════════════════════

  readonly ETIQUETAS: any = {
    SALE: 'monto vendido', FINANCED: 'saldo a financiar', COLLECTED: 'lo cobrado', SETTLED: 'descontado por liquidación',
    PERCENTAGE: 'porcentaje', FIXED: 'monto fijo',
    NO_RECOURSE: 'sin recurso', RECOURSE: 'con recurso', MIXED: 'mixto',
    ON_SALE: 'al vender', ON_COLLECTION: 'al cobrar', ON_DUE_DATE: 'al vencer la cuota', ON_INSTALLMENT_PAID: 'al terminar de pagar la cuota',
    COMPRA_DEUDA: 'cesión de deuda', COBRANZA: 'servicio de cobranza',
    NEXT_BUSINESS_DAY: 'se paga el hábil siguiente', SAME_DAY: 'se paga ese mismo día',
    INITIAL: 'con la inicial', SETTLEMENT: 'al liquidar'
  };

  eti = (v: any): string => this.ETIQUETAS[v] || (v ?? '—');

  /** PLZ-03: «1 día», «15 días». Antes: 1 día se leía «24 horas» (la minuta del 13-oct pidió días). */
  textoDias = (dias: number): string => nombreDelPlazo({ unidad: 'DIAS', valor: Number(dias) || 0 });

  /** El plazo con su unidad: «24 horas», «1 día». */
  textoPlazo = (p: any): string => (p && plazoDe(p).valor ? nombreDelPlazo(plazoDe(p)) : '—');

  /** El plazo que trae la vigente, sea del esquema nuevo (rates) o del viejo (comisión + frecuencia). */
  private plazoDeVigente = (v: any): any => {
    const rates = (v?.settlement?.rates || []).filter((r: any) => r.status !== false);
    const p = rates.find((r: any) => r.is_default) || rates[0];
    if (p) return p;
    if (v?.commission?.value == null) return null;
    const dias = ({ DAILY: 1, WEEKLY: 7, BIWEEKLY: 15, MONTHLY: 30 } as any)[v?.settlement?.frequency] || 7;
    return { days: dias, type_calc: v.commission.type_calc, commission_value: v.commission.value,
             base: v.commission.base };
  };

  /**
   * La lista de cambios respecto de la versión vigente.
   * 🔴 Es lo que evita el error más caro: crear una versión nueva creyendo que se cambió una
   * cosa y haber cambiado otra. El formulario arranca copiado de la vigente.
   */
  get cambios(): { campo: string; antes: string; ahora: string }[] {
    if (!this.vigente) return [];
    const v = this.vigente, f = this.form;
    const filas: { campo: string; antes: string; ahora: string }[] = [];
    const cmp = (campo: string, antes: any, ahora: any) => {
      const a = String(antes ?? '—'), b = String(ahora ?? '—');
      if (a !== b) filas.push({ campo, antes: a, ahora: b });
    };

    const pv = this.plazoDeVigente(v);
    cmp('Plazo de cobro', pv ? this.textoPlazo(pv) : '—', this.textoPlazo(f.plazo));
    cmp('Forma de cálculo', this.eti(pv?.type_calc), this.eti(f.plazo.type_calc));
    cmp('Base de cálculo', this.eti(pv?.base), this.eti(f.plazo.base));
    cmp('Valor de la comisión', pv?.commission_value ?? '—', f.plazo.commission_value ?? '—');
    cmp('Esquema de liquidación', this.eti(v.business_model || (MOMENTOS_COBRANZA.includes(v.settlement?.moment) ? 'COBRANZA' : 'COMPRA_DEUDA')),
        this.eti(f.business_model));
    cmp('Momento de liquidación', v.business_model === 'COMPRA_DEUDA' ? '—' : this.eti(this.momentoDe(v)),
        f.business_model === 'COBRANZA' ? this.eti(this.momentoDe({ settlement: { moment: f.moment } })) : '—');
    cmp('Día no hábil', this.eti(v.settlement?.business_day_rule || 'NEXT_BUSINESS_DAY'), this.eti(f.settlement.business_day_rule));
    cmp('Días de diferimiento', v.settlement?.lag_days ?? 0, 0);
    cmp('Cargo por uso', v.flat_fee?.value ?? 0, Number(f.flat_fee.value) || 0);
    cmp('Cobro del cargo por uso', this.eti(v.flat_fee?.charged_with || 'SETTLEMENT'),
        this.eti(f.flat_fee.charged_with));
    cmp('Referencia del contrato', v.contract_reference || '—', f.contract_reference || '—');

    // Los conceptos se comparan como texto ordenado: lo que importa es si la
    // lista cambió, no en qué orden se cargaron.
    const resumir = (lista: any[]) => (lista || [])
      .filter(c => c?.concept)
      .map(c => `${c.concept} ${c.value}${c.type_calc === 'PERCENTAGE' ? '%' : ''}` +
                `${c.status === false ? ' (pausado)' : ''}`)
      .sort().join(', ') || 'ninguno';
    cmp('Otros conceptos', resumir(v.charges), resumir(f.charges));

    // Los modelos: una fila por cada uno que cambió, no los cinco siempre.
    for (const d of this.MODELOS_DEF) {
      const mv = (v.modelos || []).find((m: any) => m.codigo === d.codigo);
      const mf = f.modelos.find((m: any) => m.codigo === d.codigo);
      cmp(`Modelo · ${d.nombre}`, this.textoModelo(mv), this.textoModelo(mf));
    }
    return filas;
  }

  textoModelo = (m: any): string => {
    if (!m?.usa) return 'no se usa';
    if (!m.cobra) return 'se usa, no se cobra';
    const monto = m.cargo?.value ?? '—';
    const forma = m.cargo?.type_calc === 'PERCENTAGE' ? '%' : '';
    const cuando = m.cargo?.charged_with === 'INITIAL' ? 'con la inicial' : 'al liquidar';
    return `se usa, se cobra ${monto}${forma} · ${cuando}`;
  };

  /** Crear una versión idéntica a la vigente casi siempre es un error. Solo en 'version'. */
  get sinCambios(): boolean { return !this.esPropuesta && !!this.vigente && this.cambios.length === 0; }

  // ══ EL RESUMEN de «Revisar» ═════════════════════════════════════════════════

  get resumenPlazo(): string {
    const p = this.form.plazo;
    const valor = p.type_calc === 'FIXED' ? `$ ${p.commission_value ?? '—'}` : `${p.commission_value ?? '—'} %`;
    const base = p.type_calc === 'PERCENTAGE'
      ? ' ' + (({ SALE: 'sobre el monto vendido', FINANCED: 'sobre el saldo a financiar',
                  COLLECTED: 'sobre lo cobrado', SETTLED: 'descontado por liquidación' } as any)[p.base] || '') : ' fijo';
    return `${p.label?.trim() || this.textoPlazo(p)} · ${valor}${base}`;
  }

  get resumenCobros(): string {
    // el mismo armado que la pestaña Condiciones: monto, base y cuándo de cada cobro (Mario, 27-oct)
    return cargosDeComercio(this.form).map((c: any) =>
      `${c.concepto} ${c.monto} ${c.base}, ${c.cuando}${c.pausado ? ' (pausado)' : ''}`).join(' · ') || 'Ninguno';
  }

  get resumenModelos(): string {
    const usados = this.form.modelos.filter((m: any) => m.usa);
    if (!usados.length) return 'Ninguno';
    return usados.map((m: any) => `${this.nombreModelo(m.codigo)}${m.cobra
      ? ` (se cobra ${m.cargo.value ?? '—'}${m.cargo.type_calc === 'PERCENTAGE' ? ' %' : ''})` : ''}`).join(' · ');
  }

  get resumenRiesgo(): string {
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_DUE_DATE') {
      return 'Servicio de cobranza · después que vence la cuota (cuota completa, pague o no el cliente)';
    }
    if (this.form.business_model === 'COBRANZA' && this.form.moment === 'ON_INSTALLMENT_PAID') {
      return 'Servicio de cobranza · después que el cliente termina de pagar la cuota (cuota completa)';
    }
    return this.form.business_model === 'COBRANZA'
      ? 'Servicio de cobranza · después que el cliente paga (liquidación continua)'
      : 'Cesión de deuda · se le paga en el plazo, sin recurso';
  }

  get resumenLiquidacion(): string {
    const desde = ({ ON_DUE_DATE: 'Vencimiento de la cuota', ON_INSTALLMENT_PAID: 'Pago completo de la cuota' } as any)[this.form.moment] || 'Cobro';
    return `${this.form.business_model === 'COBRANZA' ? desde : 'Venta'} + ${this.textoPlazo(this.form.plazo)} · ` +
      (this.form.settlement.business_day_rule === 'SAME_DAY'
        ? 'en día no hábil, ese mismo día' : 'en día no hábil, el hábil siguiente');
  }

  // ══ CONCEPTOS DE COBRO ══════════════════════════════════════════════════════

  /** «Agregar un cobro»: agrega un renglón editable a la MISMA tabla — no hay
   *  formulario aparte. La validación (concepto y monto obligatorios) se ve
   *  en el propio renglón, bajo cada campo (`faltaConcepto` no existe: se
   *  calcula en la plantilla con lo que ya tiene el renglón). */
  agregarCobro = (): void => {
    this.form.charges.push({ concept: '', description: '', type_calc: 'FIXED', value: null,
                             base: 'SALE', charged_with: 'SETTLEMENT', status: true });
    this.guardarBorrador();
  };

  quitarConcepto = (i: number): void => { this.form.charges.splice(i, 1); this.guardarBorrador(); };

  alternarConcepto = (i: number): void => {
    this.form.charges[i].status = this.form.charges[i].status === false;
    this.guardarBorrador();
  };

  /** Cuánto sale un concepto sobre la venta simulada. */
  montoConcepto = (c: any): number => {
    if (c?.status === false) return 0;
    const v = Number(c?.value) || 0;
    if (c?.type_calc === 'FIXED') return v;
    return this.montoBase(c?.base) * v / 100;
  };

  get totalConceptos(): number {
    return (this.form.charges || []).reduce((s: number, c: any) => s + this.montoConcepto(c), 0);
  }

  /** Los conceptos de una condición cualquiera, para comparar contra la vigente. */
  totalConceptosDe = (lista: any[]): number =>
    (lista || []).reduce((s: number, c: any) => s + this.montoConcepto(c), 0);

  // ══ MODELOS QUE USA EL COMERCIO ═════════════════════════════════════════════
  //
  // Por ahora quedan PACTADOS: la liquidación todavía no descuenta el cobro por
  // modelo. Se muestra y se guarda, para cuando sí lo haga.

  alternarUsaModelo = (m: any): void => {
    m.usa = !m.usa;
    if (!m.usa) m.cobra = false;
    this.guardarBorrador();
  };

  alternarCobraModelo = (m: any): void => {
    if (!m.usa) return;
    m.cobra = !m.cobra;
    this.guardarBorrador();
  };
}
